/* eslint-disable max-lines */
import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { PLACEMENT_CANDIDATE_BAN_ACTION } from "@src/appV2/redesign/PlacementCandidate/types";
import {
  BAN_DISPUTE_STATUS,
  DISPUTE_REASON,
} from "@src/appV2/redesign/Placements/types/createBanDispute.schema";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";

export const interviewsApiUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/interviews`;

export enum InterviewStatus {
  PENDING = "Pending",
  BOOKED = "Booked",
  CANCELLED = "Cancelled",
  RESCHEDULED = "Rescheduled",
  ATTENDANCE_CONFIRMED = "AttendanceConfirmed",
  NO_CALL_NO_SHOW = "NoCallNoShow",
}

export const INTERVIEW_SCHEDULE_STATUS = {
  UPCOMING: "Upcoming",
  IN_PROGRESS: "InProgress",
  PAST: "Past",
} as const;
export type InterviewScheduleStatus =
  (typeof INTERVIEW_SCHEDULE_STATUS)[keyof typeof INTERVIEW_SCHEDULE_STATUS];

export const INTERVIEW_VERIFICATION_STATUS = {
  VERIFIED: "VERIFIED",
  UNVERIFIED: "UNVERIFIED",
} as const;
export type InterviewVerificationStatus =
  (typeof INTERVIEW_VERIFICATION_STATUS)[keyof typeof INTERVIEW_VERIFICATION_STATUS];

export const INTERVIEW_TYPE = {
  IN_PERSON: "in-person-interview",
  PHONE_SCREEN: "phone-screen",
} as const;
export type InterviewType = (typeof INTERVIEW_TYPE)[keyof typeof INTERVIEW_TYPE];

export const INTERVIEW_ATTENDANCE_STATUS = {
  WORKER_IN_LOCATION: "WORKER_IN_LOCATION",
  WORKER_NOT_IN_LOCATION: "WORKER_NOT_IN_LOCATION",
  WORKER_LOCATION_UNCERTAIN: "WORKER_LOCATION_UNCERTAIN",
  WORKER_NOT_TRACKED: "WORKER_NOT_TRACKED",
} as const;
export type InterviewAttendanceStatus =
  (typeof INTERVIEW_ATTENDANCE_STATUS)[keyof typeof INTERVIEW_ATTENDANCE_STATUS];

export const INTERVIEW_ATTENDANCE_VERIFICATION_STATUS = {
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
} as const;
export type InterviewAttendanceVerificationStatus =
  (typeof INTERVIEW_ATTENDANCE_VERIFICATION_STATUS)[keyof typeof INTERVIEW_ATTENDANCE_VERIFICATION_STATUS];

const interviewRelationshipsSchema = z.object({
  workplace: z.object({
    data: z.object({
      type: z.literal(API_TYPES.workplace),
      id: z.string(),
    }),
  }),
  placementApplication: z.object({
    data: z.object({
      type: z.literal(API_TYPES.placementApplication),
      id: z.string(),
    }),
  }),
  placement: z.object({
    data: z.object({
      type: z.literal(API_TYPES.placement),
      id: z.string(),
    }),
  }),
  placementCandidate: z.object({
    data: z.object({
      type: z.literal(API_TYPES.placementCandidate),
      id: z.string(),
    }),
  }),
  interviewAttendanceVerification: z
    .object({
      data: z.object({
        type: z.literal(API_TYPES.interviewAttendanceVerification),
        id: z.string(),
      }),
    })
    .optional(),
  placementCandidateBan: z
    .object({
      data: z.object({
        type: z.literal(API_TYPES.placementCandidateBan),
        id: z.string(),
      }),
    })
    .optional(),
});

const interviewIncludedWorkplaceSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    formattedAddress: z.string().optional(),
    distance: z.number().optional(),
    phone: z.string().optional(),
  }),
});
type InterviewIncludedWorkplace = z.infer<typeof interviewIncludedWorkplaceSchema>;

const interviewIncludedPlacementSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    workerTypes: z.array(z.string()),
  }),
});
type InterviewIncludedPlacement = z.infer<typeof interviewIncludedPlacementSchema>;

const interviewIncludedWorkerSchema = z.object({
  type: z.literal(API_TYPES.worker),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    tmz: z.string(),
    profileImageUrl: z.string().optional(),
  }),
});
type InterviewIncludedWorker = z.infer<typeof interviewIncludedWorkerSchema>;

const interviewIncludedInterviewAttendanceVerificationSchema = z.object({
  type: z.literal(API_TYPES.interviewAttendanceVerification),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(INTERVIEW_ATTENDANCE_VERIFICATION_STATUS),
    interviewerName: z.string(),
    attendanceDescription: z.string(),
    hasEvidenceFile: z.boolean(),
    resolvedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
  }),
});

const interviewIncludedPlacementCandidateBanSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    action: z.nativeEnum(PLACEMENT_CANDIDATE_BAN_ACTION).optional(),
    revokedAt: z.string().datetime().optional(),
    startsAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    dispute: z
      .object({
        status: z.nativeEnum(BAN_DISPUTE_STATUS),
        reason: z.nativeEnum(DISPUTE_REASON),
        description: z.string(),
      })
      .optional(),
  }),
  meta: z.object({
    isExcused: z.boolean(),
  }),
});
type InterviewIncludedPlacementCandidateBan = z.infer<
  typeof interviewIncludedPlacementCandidateBanSchema
>;

const interviewIncludedSchema = z.array(
  z.discriminatedUnion("type", [
    interviewIncludedWorkplaceSchema,
    interviewIncludedPlacementSchema,
    interviewIncludedWorkerSchema,
    interviewIncludedInterviewAttendanceVerificationSchema,
    interviewIncludedPlacementCandidateBanSchema,
  ])
);

export const interviewSchema = z.object({
  id: z.string(),
  type: z.literal(API_TYPES.interview),
  attributes: z.object({
    start: z.string(),
    end: z.string(),
    status: z.nativeEnum(InterviewStatus),
  }),
  relationships: interviewRelationshipsSchema,
  meta: z.object({
    scheduleStatus: z.nativeEnum(INTERVIEW_SCHEDULE_STATUS).optional(),
  }),
});

export type Interview = z.infer<typeof interviewSchema>;

export const getInterviewsResponseSchema = z.object({
  data: z.array(interviewSchema),
  included: interviewIncludedSchema.optional(),
  links: z.object({
    nextCursor: z.string().optional(),
    prevCursor: z.string().optional(),
  }),
});

export type GetInterviewsResponse = z.infer<typeof getInterviewsResponseSchema>;

export const getInterviewsRequestSchema = z.object({
  page: z.object({
    size: z.number(),
    cursor: z.string().optional(),
  }),
  sort: z.string().optional(),
  filter: z.object({
    start: z
      .object({
        gt: z.string(),
      })
      .optional(),
    end: z
      .object({
        lt: z.string(),
      })
      .optional(),
    workerId: z.string().optional(),
    status: z.nativeEnum(InterviewStatus).optional(),
    "workerOutcome.dismissedAt": z
      .object({
        isNull: z.boolean().optional(),
      })
      .strict()
      .optional(),
    "workerOutcome.result": z
      .object({
        isNull: z.boolean().optional(),
      })
      .strict()
      .optional(),
    "placementApplication.status": z.string().optional(),
    connectedToPlacementId: z.string().optional(),
  }),
  include: z.string().optional(),
});

interface FetchInterviewsParams {
  sort?: string | undefined;
  filter: z.infer<typeof getInterviewsRequestSchema>["filter"];
  pageSize?: number;
  include?: string;
}

export type GetInterviewsRequest = z.infer<typeof getInterviewsRequestSchema>;

export function useFetchPaginatedInterviews(
  params: FetchInterviewsParams,
  options: UseInfiniteQueryOptions<GetInterviewsResponse, AxiosError> = {}
) {
  const { filter, sort, pageSize = 100, include } = params;

  return useInfiniteQuery({
    queryKey: [interviewsApiUrl, filter, pageSize],
    queryFn: async ({
      pageParam: pageParameter = undefined,
    }: QueryFunctionContext<QueryKey, string | undefined>) => {
      const nonEmptyFilter = Object.fromEntries(
        Object.entries(filter ?? {}).filter(([_, value]) => value)
      );
      const response = await get({
        url: interviewsApiUrl,
        queryParams: {
          page: {
            size: pageSize,
            cursor: pageParameter,
          },
          filter: nonEmptyFilter,
          sort,
          include,
        },
        responseSchema: getInterviewsResponseSchema,
        requestSchema: getInterviewsRequestSchema,
      });

      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage.links?.nextCursor ?? undefined,
    ...options,
  });
}

export function transformResponseIntoInterview(
  interview: z.infer<typeof interviewSchema>,
  includedMetadata: z.infer<typeof interviewIncludedSchema>,
  workerTimezone: string
) {
  const workplace = includedMetadata.find(
    (included): included is InterviewIncludedWorkplace =>
      included.type === API_TYPES.workplace &&
      included.id === interview.relationships.workplace.data.id
  );

  const worker = includedMetadata.find(
    (included): included is InterviewIncludedWorker =>
      included.type === API_TYPES.workplace &&
      included.id === interview.relationships.workplace.data.id
  );

  const placement = includedMetadata.find(
    (included): included is InterviewIncludedPlacement =>
      included.type === API_TYPES.placement &&
      included.id === interview.relationships.placement.data.id
  );

  const placementCandidateBan = includedMetadata.find(
    (included): included is InterviewIncludedPlacementCandidateBan =>
      included.type === API_TYPES.placementCandidateBan &&
      included.id === interview.relationships.placementCandidateBan?.data.id
  );

  return {
    id: interview.id,
    type: interview.type,
    ...interview.attributes,
    scheduleStatus: interview.meta?.scheduleStatus,
    isBanExcused: placementCandidateBan?.meta.isExcused ?? false,
    start: utcToZonedTime(interview.attributes.start, workerTimezone),
    end: utcToZonedTime(interview.attributes.end, workerTimezone),
    placementId: interview.relationships?.placement?.data?.id,
    placementApplicationId: interview.relationships?.placementApplication?.data?.id,
    ...(workplace && {
      workplace: {
        id: workplace.id,
        name: workplace.attributes.name,
        formattedAddress: workplace.attributes.formattedAddress,
        distance: workplace.attributes.distance,
        phone: workplace.attributes.phone,
      },
    }),
    ...(placement && {
      placement: {
        id: placement.id,
        workerTypes: placement.attributes.workerTypes,
      },
    }),
    ...(worker ? { worker: { id: worker.id, ...worker.attributes } } : undefined),
    placementCandidateId: interview.relationships?.placementCandidate?.data?.id,
  };
}

export type InterviewWithDetails = ReturnType<typeof transformResponseIntoInterview>;

export type Bookings =
  | { type: "shift"; data: Shift }
  | { type: "interview"; data: InterviewWithDetails };
/* eslint-enable max-lines */
